"use client";
import { useState, useEffect, useRef } from "react";
import { Send, ThumbsUp, ThumbsDown, X, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { REST_API_URL } from "../../api/config";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

export default function HalperChatView() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const [userStatus, setUserStatus] = useState(null);
  const [showSubModal, setShowSubModal] = useState(false);
  const chatRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    loadMessages();
    fetchPaymentAndUser();
  }, []);

  const fetchPaymentAndUser = async () => {
    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      const headers = buildHeaders(token);

      const [userRes] = await Promise.all([
        fetch(`${REST_API_URL}/auth/userinfo/`, { headers }),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserStatus(userData?.user?.status ?? null);
      }
    } catch (e) {
      console.error("❌ Failed to fetch payment/user info:", e);
    }
  };

  const loadMessages = async () => {
    try {
      setInitialLoading(true);
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      const headers = buildHeaders(token);

      const res = await fetch(`${API_BASE}/messaging/halper-messages-list/`, {
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const sorted = data.results.sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at),
      );

      const formatted = sorted.flatMap((msg) => [
        {
          id: msg.id,
          from: "user",
          text: msg.text,
          created_at: msg.created_at,
        },
        {
          id: msg.id,
          from: "halper",
          text: msg.answer,
          reaction: msg.reaction,
          created_at: msg.created_at,
        },
      ]);

      setMessages(formatted);
      localStorage.setItem("halper_chat_messages", JSON.stringify(formatted));
    } catch (err) {
      console.error("❌ Failed to load messages:", err);
      const cached = localStorage.getItem("halper_chat_messages");
      if (cached) setMessages(JSON.parse(cached));
    } finally {
      setInitialLoading(false);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim() || loading) return;
    const textToSend = messageText.trim();

    if (!paymentCheck?.has_active_subscription) {
      setShowSubModal(true);
      return;
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", "halper_contact_support");
    }

    setMessages((prev) => [
      ...prev,
      { from: "user", text: textToSend, created_at: new Date().toISOString() },
    ]);
    setInputValue("");
    setLoading(true);

    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");
    const headers = buildHeaders(token);

    try {
      const response = await fetch(`${API_BASE}/messaging/halper-response/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: 637,
          text: textToSend,
          reaction: "neutral",
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      setMessages((prev) => [
        ...prev,
        {
          id: data.id,
          from: "halper",
          text: data.answer || "Got it 👍",
          reaction: data.reaction,
          created_at: new Date().toISOString(),
        },
      ]);

      localStorage.setItem(
        "halper_chat_messages",
        JSON.stringify([
          ...messages,
          { text: textToSend },
          { text: data.answer },
        ]),
      );
    } catch (e) {
      console.error("❌ Error sending message:", e);
      setMessages((prev) => [
        ...prev,
        {
          from: "halper",
          text: "Something went wrong. Please try again later.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendReaction = async (id, reactionType) => {
    try {
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      const headers = buildHeaders(token);

      const res = await fetch(
        `${API_BASE}/messaging/halper-message-react/${id}/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ reaction: reactionType }),
        },
      );
      const data = await res.json();

      if (res.ok) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === id && msg.from === "halper"
              ? { ...msg, reaction: data.reaction }
              : msg,
          ),
        );
      } else {
        console.error("Reaction error:", data);
      }
    } catch (err) {
      console.error("❌ Failed to send reaction:", err);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    try {
      const userTimeZone = getUserTimeZone();
      return new Date(timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: userTimeZone,
      });
    } catch {
      return "";
    }
  };

  const isWorker = userStatus === "worker";

  return (
    <>
      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <X size={18} />
              </button>

              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>

              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                {isWorker ? "Subscription Required" : "Upgrade Your Plan"}
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                {isWorker
                  ? "You need an active subscription to use Halper chat."
                  : "Unlock Halper chat and all premium features with a subscription."}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                {!isWorker && (
                  <button
                    onClick={() => {
                      setShowSubModal(false);
                      navigate("/profile?section=Settings&tab=subscription");
                    }}
                    className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#5563F6] to-[#8B54FF] text-white text-sm font-medium hover:opacity-90 transition"
                  >
                    View Plans
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="relative bg-white rounded-[16px] w-[full] h-[993px] shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F6F6F6] text-[#5563F6] font-semibold text-lg">
              H
            </div>
            <span className="text-[#302B2B] font-medium text-[15px]">
              Halper chat
            </span>
          </div>
        </div>

        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto px-6 py-6 space-y-4 scroll-smooth"
        >
          {initialLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5563F6]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-[#302B2B] opacity-70">
              <p className="text-[16px]">Start the conversation 👋</p>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div key={idx} className="flex flex-col">
                <div
                  className={`flex ${
                    msg.from === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[70%] px-4 py-2 rounded-2xl text-sm ${
                      msg.from === "user"
                        ? "bg-[#5563F6] text-white"
                        : "bg-[#F6F6F6] text-[#302B2B]"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>

                <div
                  className={`text-xs text-gray-400 mt-1 ${msg.from === "user" ? "text-right mr-2" : "text-left ml-2"}`}
                >
                  {formatTime(msg.created_at)}
                </div>

                {msg.from === "halper" && (
                  <div className="flex gap-3 mt-1 ml-2">
                    <button
                      onClick={() => sendReaction(msg.id, "like")}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#5563F6]"
                    >
                      <span
                        className={`p-1 rounded-full transition ${
                          msg.reaction === "like"
                            ? "bg-[#5563F6] text-white"
                            : "bg-transparent"
                        }`}
                      >
                        <ThumbsUp size={14} />
                      </span>
                    </button>

                    <button
                      onClick={() => sendReaction(msg.id, "dislike")}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#E1090C]"
                    >
                      <span
                        className={`p-1 rounded-full transition ${
                          msg.reaction === "dislike"
                            ? "bg-[#E1090C] text-white"
                            : "bg-transparent"
                        }`}
                      >
                        <ThumbsDown size={14} />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ))
          )}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-[#F6F6F6] text-[#302B2B] px-4 py-2 rounded-2xl text-sm opacity-70">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-[#5863ED] rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-[#5863ED] rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-2 h-2 bg-[#5863ED] rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 bg-[#F6F6F6] rounded-full px-4 py-2">
            <input
              type="text"
              placeholder="Type a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage(inputValue)}
              className="flex-1 bg-transparent outline-none text-sm text-[#302B2B] placeholder-[#9A99AB]"
            />
            <button
              onClick={() => sendMessage(inputValue)}
              disabled={loading}
              className="bg-[#5563F6] hover:bg-[#3d4de0] p-2 rounded-full text-white disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
